// src/pages/VendaMes.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../services/api.js";
import Swal from 'sweetalert2';
import Menu from "../../comum/Menu/menu.jsx";
import Footer from "../../comum/Footer/index.jsx";
import "./vendames.css";

import {
  FaChevronDown,
  FaChevronUp,
  FaUndoAlt,
  FaCalendarAlt,
  FaFilter,
  FaCheckDouble,
  FaTimes
} from "react-icons/fa";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

// ================= FORMATADORES E UTILITÁRIOS =================
const formatMoeda = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 }).format(v ?? 0);

const formatPercent = (v) =>
  isNaN(v) || v === null || v === undefined ? "0,00%" : `${Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

const formatLoja = (n) => String(n).padStart(2, '0');

const formatarMesExibicao = (mesAno) => {
  if (!mesAno || mesAno.length !== 4) return mesAno;
  return `${mesAno.slice(2)}/20${mesAno.slice(0, 2)}`;
};

const formatarParaInput = (mesAno) => {
  if (!mesAno || mesAno.length !== 4) return "";
  return `20${mesAno.slice(0, 2)}-${mesAno.slice(2)}`;
};

// Componente de Garrafa / Progresso Meta
const MetaProgressoGarrafa = ({ percentual }) => {
  const pct = Math.min(Math.max(percentual || 0, 0), 100);
  return (
    <div className="bottle-wrapper">
      <div className="bottle-cap"></div>
      <div className="bottle-container" title={`Atingido: ${pct.toFixed(1)}%`}>
        <div className="bottle-fill" style={{ height: `${pct}%` }}></div>
      </div>
      <span className="bottle-badge">{pct.toFixed(1)}%</span>
    </div>
  );
};

// Modal Genérico Padronizado
function ModalGenerico({ show, onClose, title, children }) {
  if (!show) return null;
  return (
    <div className="modal fade show" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(15, 23, 42, 0.75)', zIndex: 1200, backdropFilter: 'blur(4px)' }}>
      <div className="modal-dialog modal-md modal-dialog-centered">
        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '14px', overflow: 'hidden' }}>
          <div className="modal-header bg-custom-red text-white py-3 px-4">
            <h5 className="modal-title font-weight-bold fs-6 m-0 text-white">{title}</h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>
          <div className="modal-body p-4">{children}</div>
          <div className="modal-footer bg-light py-2 px-4">
            <button type="button" className="btn btn-custom-red btn-sm px-4 fw-semibold" onClick={onClose}>Fechar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Modal Ampliação de Gráfico (Zoom em Tela Cheia)
function ChartZoomModal({ show, onClose, title, children }) {
  if (!show) return null;
  return (
    <div className="chart-zoom-modal-overlay">
      <div className="chart-zoom-modal-content">
        <div className="chart-zoom-modal-header">
          <h5 className="m-0 fs-6 fw-bold text-dark">{title}</h5>
          <button type="button" className="btn-close" onClick={onClose}></button>
        </div>
        <div className="chart-zoom-modal-body">
          {children}
        </div>
      </div>
    </div>
  );
}

// Modal Loading Padronizado
function LoadingModal({ show, message }) {
  if (!show) return null;
  return (
    <div className="loading-modal-overlay">
      <div className="loading-modal-content">
        <div className="spinner-border text-custom-red" role="status" style={{ width: '2.5rem', height: '2.5rem' }}>
          <span className="visually-hidden">A carregar...</span>
        </div>
        <div className="loading-text mt-3 fw-bold text-secondary">{message}</div>
      </div>
    </div>
  );
}

const VendaMes = () => {
  const [showModal, setShowModal] = useState(false);
  const [showLojaModal, setShowLojaModal] = useState(false);
  const [zoomChart, setZoomChart] = useState(null);
  const [activeTabMobile, setActiveTabMobile] = useState('dia'); // 🔹 Estado da aba mobile
  
  const [mesSelecionado, setMesSelecionado] = useState("");
  const [dadosMes, setDadosMes] = useState([]);
  const [detalhesDia, setDetalhesDia] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("A carregar dados...");
  const [lojas, setLojas] = useState([]);
  const [lojasSelecionadas, setLojasSelecionadas] = useState([]);

  const navigate = useNavigate();
  const isMobile = window.innerWidth < 768;

  // Verifica Acesso
  useEffect(() => {
    const usuario = localStorage.getItem("chapa");
    const permiteVendas = localStorage.getItem("permiteVendas");

    if (!usuario) {
      navigate("/arbor/login");
      return;
    }

    if (permiteVendas !== "S") {
      Swal.fire({
        icon: 'error',
        title: 'Acesso Negado',
        text: 'Não é permitido acessar as vendas.',
        confirmButtonText: 'OK'
      }).then(() => {
        navigate("/arbor/home");
      });
    }
  }, [navigate]);

  // Mês Atual Padrão
  useEffect(() => {
    const hoje = new Date();
    const aa = String(hoje.getFullYear()).slice(2);
    const mm = String(hoje.getMonth() + 1).padStart(2, "0");
    setMesSelecionado(`${aa}${mm}`);
  }, []);

  // Busca Lista de Lojas Disponíveis
  useEffect(() => {
    const fetchLojas = async () => {
      try {
        const { data } = await api.get('/lojas/localizacao');
        const todasLojas = (data || []).map(loja => formatLoja(loja.cod_emp));
        setLojas(data || []);
        setLojasSelecionadas(todasLojas);
      } catch (e) {
        console.error("Erro ao carregar lojas:", e);
        setLojas([]);
      }
    };
    fetchLojas();
  }, []);

  // Carrega Vendas por Dia do Mês
  const carregarMes = useCallback(async (mesRef, lojasFiltro) => {
    const usuario = localStorage.getItem("chapa");
    if (!mesRef || !usuario) return;

    setLoading(true);
    setLoadingMessage("A carregar evolução diária das vendas...");
    const lojasParam = (lojasFiltro || []).length > 0 ? lojasFiltro.join(',') : '';

    try {
      const { data } = await api.get(`venda/mes/acum`, {
        params: {
          mes: mesRef,
          usuario,
          ...(lojasParam ? { busca: lojasParam } : {})
        }
      });
      setDadosMes(Array.isArray(data) ? data : []);
      setDetalhesDia({});
    } catch (e) {
      console.error("Erro ao carregar mês:", e);
      setDadosMes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (mesSelecionado && lojasSelecionadas.length > 0) {
      carregarMes(mesSelecionado, lojasSelecionadas);
    }
  }, [mesSelecionado, lojasSelecionadas, carregarMes]);

  // Busca Detalhamento por Loja em um Dia Específico
  const carregarDia = async (dia, numdia) => {
    const usuario = localStorage.getItem("chapa");
    if (!usuario) return;

    if (detalhesDia[numdia]) {
      const copia = { ...detalhesDia };
      delete copia[numdia];
      setDetalhesDia(copia);
      return;
    }

    setLoading(true);
    setLoadingMessage(`A carregar vendas por loja do Dia ${numdia}...`);
    const lojasParam = lojasSelecionadas.length > 0 ? lojasSelecionadas.join(',') : '';

    try {
      const { data } = await api.get(`venda/mes/loja`, {
        params: {
          mes: mesSelecionado,
          usuario,
          dia,
          ...(lojasParam ? { busca: lojasParam } : {})
        }
      });
      setDetalhesDia(prev => ({ ...prev, [numdia]: data || [] }));
    } catch (e) {
      console.error("Erro ao carregar lojas do dia:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleLojaToggle = (cod_emp) => {
    const codStr = formatLoja(cod_emp);
    setLojasSelecionadas(prev => 
      prev.includes(codStr) ? prev.filter(l => l !== codStr) : [...prev, codStr]
    );
  };

  const handleSelectAllLojas = () => {
    setLojasSelecionadas(lojas.map(loja => formatLoja(loja.cod_emp)));
  };

  const handleClearAllLojas = () => {
    setLojasSelecionadas([]);
  };

  // CÁLCULO DOS TOTAIS DO PERÍODO
  const totalVenda = dadosMes.reduce((acc, curr) => acc + (curr.venda || 0), 0);
  const totalMeta = dadosMes.reduce((acc, curr) => acc + (curr.meta || 0), 0);
  const totalVlrMargem = dadosMes.reduce((acc, curr) => acc + ((curr.venda || 0) * ((curr.margem || 0) / 100)), 0);
  
  const totalVendaAcum = dadosMes.length > 0 ? (dadosMes[dadosMes.length - 1].vda_vlr_acm || 0) : 0;
  const totalMetaAcum = dadosMes.length > 0 ? (dadosMes[dadosMes.length - 1].vda_mta_acm || 0) : 0;

  const totalAtingidoDia = totalMeta > 0 ? (totalVenda / totalMeta) * 100 : 0;
  const totalAtingidoPeriodo = totalMetaAcum > 0 ? (totalVendaAcum / totalMetaAcum) * 100 : 0;
  const totalMargem = totalVenda > 0 ? (totalVlrMargem / totalVenda) * 100 : 0;

  // CHART 1: Venda x Meta Dia (% Atingido & % Margem Real)
  const chartData1 = {
    labels: dadosMes.map(d => `Dia ${d.numdia}`),
    datasets: [
      {
        type: 'bar',
        label: '% Atingido Dia',
        data: dadosMes.map(d => d.atingido ?? 0),
        backgroundColor: dadosMes.map(d => (d.atingido ?? 0) >= 100 ? 'rgba(16, 185, 129, 0.85)' : 'rgba(2, 132, 199, 0.85)'),
        borderColor: dadosMes.map(d => (d.atingido ?? 0) >= 100 ? 'rgba(16, 185, 129, 1)' : 'rgba(2, 132, 199, 1)'),
        borderWidth: 1,
        borderRadius: 4,
        yAxisID: 'y'
      },
      {
        type: 'line',
        label: '% Margem Real',
        data: dadosMes.map(d => d.margem ?? 0),
        borderColor: '#f59e0b',
        backgroundColor: '#f59e0b',
        borderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 5,
        tension: 0.3,
        yAxisID: 'y1'
      }
    ]
  };

  const getChartOptions1 = (isZoom = false) => ({
    responsive: true,
    maintainAspectRatio: !isZoom,
    layout: { padding: { top: 25 } },
    plugins: {
      legend: { position: 'top', labels: { font: { size: isZoom ? 12 : 10, weight: '600' } } },
      title: { display: true, text: 'Venda x Meta Dia (% Atingido & % Margem)', font: { size: isZoom ? 15 : 11, weight: 'bold' }, color: '#0f172a' },
      tooltip: {
        backgroundColor: '#0f172a',
        padding: 8,
        callbacks: {
          label: (ctx) => {
            const item = dadosMes[ctx.dataIndex];
            if (!item) return `${ctx.dataset.label}: ${ctx.raw}%`;
            if (ctx.dataset.type === 'line') return `Margem Dia: ${formatPercent(item.margem)}`;
            return [
              `Venda: ${formatMoeda(item.venda)}`,
              `Meta: ${formatMoeda(item.meta)}`,
              `% Atingido: ${formatPercent(item.atingido)}`
            ];
          }
        }
      },
      datalabels: {
        color: (ctx) => (ctx.dataset.type === 'line' ? '#b45309' : '#0f172a'),
        font: { weight: 'bold', size: isZoom ? 10 : (isMobile ? 6 : 7) },
        anchor: 'end',
        align: 'top',
        offset: 2,
        rotation: (ctx) => (ctx.dataset.type === 'line' ? 0 : -90),
        formatter: (v) => `${(v ?? 0).toFixed(1)}%`
      }
    },
    scales: {
      x: { ticks: { font: { size: isZoom ? 10 : 7.5 } }, grid: { display: false } },
      y: { type: 'linear', display: true, position: 'left', beginAtZero: true, max: 130, title: { display: true, text: 'Atingido (%)', font: { weight: '600', size: 9 } }, ticks: { callback: (v) => v + '%', font: { size: 8 } } },
      y1: { type: 'linear', display: true, position: 'right', beginAtZero: true, max: 130, title: { display: true, text: 'Margem (%)', font: { weight: '600', size: 9 } }, ticks: { callback: (v) => v + '%', font: { size: 8 } }, grid: { drawOnChartArea: false } }
    }
  });

  // CHART 2: Margem Real vs Meta Margem (Rótulos Perfeitamente Deslocados)
  const chartDataMargem = {
    labels: dadosMes.map(d => `Dia ${d.numdia}`),
    datasets: [
      {
        type: 'line',
        label: 'Margem Real (%)',
        data: dadosMes.map(d => d.margem ?? 0),
        borderColor: '#10b981',
        backgroundColor: '#10b981',
        borderWidth: 2,
        pointRadius: 4,
        tension: 0.3
      },
      {
        type: 'line',
        label: 'Meta Margem (%)',
        data: dadosMes.map(d => d.margemmeta ?? 0),
        borderColor: '#6366f1',
        backgroundColor: '#6366f1',
        borderWidth: 2,
        borderDash: [4, 4],
        pointRadius: 3,
        tension: 0.3
      }
    ]
  };

  const getChartOptionsMargem = (isZoom = false) => ({
    responsive: true,
    maintainAspectRatio: !isZoom,
    layout: { padding: { top: 25 } },
    plugins: {
      legend: { position: 'top', labels: { font: { size: isZoom ? 12 : 10, weight: '600' } } },
      title: { display: true, text: 'Evolução da Margem Real vs. Meta da Margem (%)', font: { size: isZoom ? 15 : 11, weight: 'bold' }, color: '#0f172a' },
      tooltip: { backgroundColor: '#0f172a', padding: 8 },
      datalabels: {
        color: (ctx) => (ctx.datasetIndex === 0 ? '#047857' : '#4338ca'),
        font: { weight: 'bold', size: isZoom ? 10 : (isMobile ? 6.5 : 7.5) },
        anchor: (ctx) => (ctx.datasetIndex === 0 ? 'end' : 'start'),
        align: (ctx) => (ctx.datasetIndex === 0 ? 'top' : 'bottom'),
        offset: 5,
        formatter: (v) => `${(v ?? 0).toFixed(1)}%`
      }
    },
    scales: {
      x: { ticks: { font: { size: isZoom ? 10 : 7.5 } }, grid: { display: false } },
      y: { beginAtZero: true, max: 50, ticks: { callback: (v) => v + '%', font: { size: 8 } } }
    }
  });

  // CHART 3: Meta Atingida por Período / Acumulada
  const chartDataAcum = {
    labels: dadosMes.map(d => `Dia ${d.numdia}`),
    datasets: [
      {
        type: 'bar',
        label: '% Atingido Período',
        data: dadosMes.map(d => d.atingidoper ?? 0),
        backgroundColor: dadosMes.map(d => (d.atingidoper ?? 0) >= 100 ? 'rgba(16, 185, 129, 0.85)' : 'rgba(249, 115, 22, 0.85)'),
        borderColor: dadosMes.map(d => (d.atingidoper ?? 0) >= 100 ? 'rgba(16, 185, 129, 1)' : 'rgba(249, 115, 22, 1)'),
        borderWidth: 1,
        borderRadius: 4
      }
    ]
  };

  const getChartOptionsAcum = (isZoom = false) => ({
    responsive: true,
    maintainAspectRatio: !isZoom,
    layout: { padding: { top: 25 } },
    plugins: {
      legend: { position: 'top', labels: { font: { size: isZoom ? 12 : 10, weight: '600' } } },
      title: { display: true, text: 'Meta Atingida por Período / Acumulada (%)', font: { size: isZoom ? 15 : 11, weight: 'bold' }, color: '#0f172a' },
      tooltip: { backgroundColor: '#0f172a', padding: 8 },
      datalabels: {
        color: '#0f172a',
        font: { weight: 'bold', size: isZoom ? 10 : (isMobile ? 6 : 7.5) },
        anchor: 'end',
        align: 'top',
        offset: 2,
        rotation: -90,
        formatter: (v) => `${(v ?? 0).toFixed(1)}%`
      }
    },
    scales: {
      x: { ticks: { font: { size: isZoom ? 10 : 7.5 } }, grid: { display: false } },
      y: { beginAtZero: true, max: 130, ticks: { callback: (v) => v + '%', font: { size: 8 } } }
    }
  });

  return (
    <div className="flash-vendas-layout-wrapper">
      <Menu modulo="ARBOR" />

      <LoadingModal show={loading} message={loadingMessage} />

      <button className="btn-floating-calendar" onClick={() => setShowModal(true)} title="Selecionar Período">
        <FaCalendarAlt />
      </button>

      <div className="container-fluid px-3 px-md-4 max-width-container">
        
        {/* CABEÇALHO DA PÁGINA */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 pt-2 gap-2">
          <h5 className="fw-bold m-0 text-dark fs-6">
            Consulta de Vendas do Mês - Período: <span className="text-primary">{formatarMesExibicao(mesSelecionado)}</span>
          </h5>

          <div className="d-flex align-items-center gap-2">
            <button className="btn btn-outline-primary btn-sm fw-semibold" onClick={() => setShowLojaModal(true)}>
              <FaFilter className="me-1" /> Lojas ({lojasSelecionadas.length})
            </button>
            <button className="btn btn-outline-secondary btn-sm fw-semibold" onClick={() => carregarMes(mesSelecionado, lojasSelecionadas)} disabled={loading}>
              <FaUndoAlt className="me-1" /> Atualizar
            </button>
          </div>
        </div>

        {/* 🔹 VISÃO DESKTOP: OS 3 GRÁFICOS LADO A LADO NA MESMA LINHA */}
        {!isMobile && dadosMes.length > 0 && (
          <div className="row g-2 mb-4">
            
            {/* GRÁFICO 1 */}
            <div className="col-md-4">
              <div className="chart-container position-relative h-100">
                <button
                  className="btn btn-expand-chart"
                  onClick={() => setZoomChart({ type: 1, title: 'Venda x Meta Dia (% Atingido & % Margem)' })}
                  title="Ampliar Gráfico"
                >
                  🔍 Expandir
                </button>
                <div style={{ width: "100%", height: 260 }}>
                  <Bar options={getChartOptions1(false)} data={chartData1} />
                </div>
              </div>
            </div>

            {/* GRÁFICO 2 */}
            <div className="col-md-4">
              <div className="chart-container position-relative h-100">
                <button
                  className="btn btn-expand-chart"
                  onClick={() => setZoomChart({ type: 2, title: 'Evolução da Margem Real vs. Meta da Margem (%)' })}
                  title="Ampliar Gráfico"
                >
                  🔍 Expandir
                </button>
                <div style={{ width: "100%", height: 260 }}>
                  <Line options={getChartOptionsMargem(false)} data={chartDataMargem} />
                </div>
              </div>
            </div>

            {/* GRÁFICO 3 */}
            <div className="col-md-4">
              <div className="chart-container position-relative h-100">
                <button
                  className="btn btn-expand-chart"
                  onClick={() => setZoomChart({ type: 3, title: 'Meta Atingida por Período / Acumulada (%)' })}
                  title="Ampliar Gráfico"
                >
                  🔍 Expandir
                </button>
                <div style={{ width: "100%", height: 260 }}>
                  <Bar options={getChartOptionsAcum(false)} data={chartDataAcum} />
                </div>
              </div>
            </div>

          </div>
        )}

        {/* 🔹 VISÃO MOBILE: GRÁFICO ÚNICO COM ABAS DE NAVEGAÇÃO ABAIXO */}
        {isMobile && dadosMes.length > 0 && (
          <div className="mb-4 pt-1">
            <div className="chart-container position-relative">
              {activeTabMobile === 'dia' && (
                <>
                  <button
                    className="btn btn-expand-chart"
                    onClick={() => setZoomChart({ type: 1, title: 'Venda x Meta Dia (% Atingido & % Margem)' })}
                  >
                    🔍
                  </button>
                  <div style={{ width: "100%", height: 250 }}>
                    <Bar options={getChartOptions1(false)} data={chartData1} />
                  </div>
                </>
              )}

              {activeTabMobile === 'margem' && (
                <>
                  <button
                    className="btn btn-expand-chart"
                    onClick={() => setZoomChart({ type: 2, title: 'Evolução da Margem Real vs. Meta da Margem (%)' })}
                  >
                    🔍
                  </button>
                  <div style={{ width: "100%", height: 250 }}>
                    <Line options={getChartOptionsMargem(false)} data={chartDataMargem} />
                  </div>
                </>
              )}

              {activeTabMobile === 'periodo' && (
                <>
                  <button
                    className="btn btn-expand-chart"
                    onClick={() => setZoomChart({ type: 3, title: 'Meta Atingida por Período / Acumulada (%)' })}
                  >
                    🔍
                  </button>
                  <div style={{ width: "100%", height: 250 }}>
                    <Bar options={getChartOptionsAcum(false)} data={chartDataAcum} />
                  </div>
                </>
              )}
            </div>

            {/* BARRA DE ABAS MOBILE (PADRÃO FLASH) */}
            <ul className="nav nav-pills nav-justified bg-white p-1 rounded-3 shadow-sm border mt-2">
              <li className="nav-item">
                <button
                  className={`nav-link btn-sm fw-bold py-1 ${activeTabMobile === 'dia' ? 'active bg-primary text-white' : 'text-dark'}`}
                  onClick={() => setActiveTabMobile('dia')}
                >
                  Meta Dia
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link btn-sm fw-bold py-1 ${activeTabMobile === 'margem' ? 'active bg-primary text-white' : 'text-dark'}`}
                  onClick={() => setActiveTabMobile('margem')}
                >
                  Margem
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link btn-sm fw-bold py-1 ${activeTabMobile === 'periodo' ? 'active bg-primary text-white' : 'text-dark'}`}
                  onClick={() => setActiveTabMobile('periodo')}
                >
                  Período
                </button>
              </li>
            </ul>
          </div>
        )}

        {/* RESUMO GERAL MOBILE PADRONIZADO */}
        {isMobile && dadosMes.length > 0 && (
          <div className="card mb-3 border shadow-sm rounded-3 bg-white p-3">
            <div className="small text-uppercase text-muted text-center fw-bold mb-2 pb-1 border-bottom">
              Resumo Geral do Período
            </div>
            <div className="row text-center g-2 align-items-center">
              <div className="col-5 border-end pe-1">
                <span className="x-small text-muted d-block">Venda Acum.</span>
                <strong className="text-dark font-mono-compact d-block text-truncate">
                  {formatMoeda(totalVenda)}
                </strong>
              </div>
              <div className="col-3 border-end px-1">
                <span className="x-small text-muted d-block">Atingido</span>
                <strong className={`fs-6 ${totalAtingidoPeriodo >= 100 ? "text-success" : "text-custom-red"}`}>
                  {formatPercent(totalAtingidoPeriodo)}
                </strong>
              </div>
              <div className="col-4 ps-1">
                <span className="x-small text-muted d-block">Margem</span>
                <strong className="fs-6 text-dark">{formatPercent(totalMargem)}</strong>
              </div>
            </div>
          </div>
        )}

        {/* VISÃO DESKTOP (Tabela) */}
        {!isMobile && (
          <div className="table-frozen-wrapper shadow-sm border rounded-3 mb-4">
            <table className="table table-striped table-hover align-middle m-0 table-compact-fixed">
              <thead className="table-header-frozen">
                <tr>
                  <th style={{ width: '40px' }}></th>
                  <th>Dia</th>
                  <th className="text-end" style={{ width: '130px' }}>Venda</th>
                  <th className="text-end" style={{ width: '130px' }}>Meta</th>
                  <th className="text-end" style={{ width: '90px' }}>Margem</th>
                  <th className="text-end" style={{ width: '100px' }}>Ating. Dia</th>
                  <th className="text-end" style={{ width: '100px' }}>Ating. Período</th>
                  <th className="text-center" style={{ width: '80px' }}>Progresso</th>
                </tr>
              </thead>
              <tbody>
                {/* LINHA DE TOTAL PERÍODO NO TOPO */}
                {dadosMes.length > 0 && (
                  <tr className="fw-bold table-secondary border-bottom">
                    <td></td>
                    <td className="text-uppercase text-primary">TOTAL PERÍODO</td>
                    <td className="text-end text-primary">{formatMoeda(totalVenda)}</td>
                    <td className="text-end">{formatMoeda(totalMeta)}</td>
                    <td className="text-end text-dark">{formatPercent(totalMargem)}</td>
                    <td className={`text-end ${totalAtingidoDia >= 100 ? "text-success" : "text-custom-red"}`}>
                      {formatPercent(totalAtingidoDia)}
                    </td>
                    <td className={`text-end ${totalAtingidoPeriodo >= 100 ? "text-success" : "text-custom-red"}`}>
                      {formatPercent(totalAtingidoPeriodo)}
                    </td>
                    <td className="text-center py-1">
                      <MetaProgressoGarrafa percentual={totalAtingidoPeriodo} />
                    </td>
                  </tr>
                )}

                {dadosMes.map((d) => (
                  <React.Fragment key={d.numdia}>
                    <tr onClick={() => carregarDia(d.dia, d.numdia)} style={{ cursor: "pointer" }} className="fw-semibold">
                      <td className="text-center">
                        {detalhesDia[d.numdia] ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                      </td>
                      <td className="text-dark">Dia {d.numdia}</td>
                      <td className="text-end">{formatMoeda(d.venda)}</td>
                      <td className="text-end">{formatMoeda(d.meta)}</td>
                      <td className="text-end text-dark">{formatPercent(d.margem)}</td>
                      <td className={`text-end ${d.atingido >= 100 ? "text-success" : "text-custom-red"}`}>
                        {formatPercent(d.atingido)}
                      </td>
                      <td className={`text-end ${d.atingidoper >= 100 ? "text-success" : "text-custom-red"}`}>
                        {formatPercent(d.atingidoper)}
                      </td>
                      <td className="text-center py-1">
                        <MetaProgressoGarrafa percentual={d.atingidoper} />
                      </td>
                    </tr>

                    {/* DETALHAMENTO DAS LOJAS NO DIA */}
                    {detalhesDia[d.numdia] && (
                      <tr className="bg-light">
                        <td colSpan="8" className="p-3 border-bottom">
                          <div className="bg-white p-3 rounded-3 border shadow-xs">
                            <h6 className="fw-bold text-dark border-bottom pb-2 mb-3">
                              Vendas por Loja - Dia {d.numdia}
                            </h6>
                            <div className="table-responsive">
                              <table className="table table-sm table-bordered align-middle text-center small m-0">
                                <thead className="bg-light">
                                  <tr>
                                    <th>Filial</th>
                                    <th>Venda</th>
                                    <th>Meta</th>
                                    <th>Margem (%)</th>
                                    <th>% Atingido</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {detalhesDia[d.numdia].map((loja, idx) => (
                                    <tr key={idx}>
                                      <td className="fw-bold">Loja {formatLoja(loja.filial)}</td>
                                      <td>{formatMoeda(loja.venda)}</td>
                                      <td>{formatMoeda(loja.meta)}</td>
                                      <td className="text-dark">{formatPercent(loja.margem)}</td>
                                      <td className={`fw-bold ${loja.atingido >= 100 ? "text-success" : "text-custom-red"}`}>
                                        {formatPercent(loja.atingido)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* VISÃO MOBILE (Cards Expansíveis) */}
        {isMobile && (
          <div className="mobile-list pb-5">
            {dadosMes.map((d) => (
              <div key={d.numdia} className="card mb-3 border-0 shadow-sm rounded-3 overflow-hidden">
                
                {/* DIA - CABEÇALHO */}
                <div 
                  className="card-header bg-white py-3 px-3 border-0 d-flex justify-content-between align-items-center" 
                  onClick={() => carregarDia(d.dia, d.numdia)} 
                  style={{ cursor: 'pointer' }}
                >
                  <span className="fw-bold text-dark fs-6">DIA {d.numdia}</span>
                  <div className="d-flex align-items-center gap-2">
                    <span className={`fw-bold fs-6 ${d.atingido >= 100 ? "text-success" : "text-custom-red"}`}>
                      {formatPercent(d.atingido)}
                    </span>
                    {detalhesDia[d.numdia] ? <FaChevronUp size={14} className="text-muted"/> : <FaChevronDown size={14} className="text-muted"/>}
                  </div>
                </div>

                {/* DIA - METRICAS UNIFORMES */}
                <div className="px-3 pb-3">
                  <div className="row g-2 text-center bg-light rounded-3 py-2 border m-0">
                    <div className="col-6 border-end border-bottom pb-1">
                      <span className="text-muted d-block x-small">Venda</span>
                      <strong className="text-dark fs-6">{formatMoeda(d.venda)}</strong>
                    </div>
                    <div className="col-6 border-bottom pb-1">
                      <span className="text-muted d-block x-small">Meta</span>
                      <strong className="text-dark fs-6">{formatMoeda(d.meta)}</strong>
                    </div>
                    <div className="col-6 border-end pt-1">
                      <span className="text-muted d-block x-small">Ating. Período</span>
                      <strong className={`fs-6 ${d.atingidoper >= 100 ? "text-success" : "text-custom-red"}`}>
                        {formatPercent(d.atingidoper)}
                      </strong>
                    </div>
                    <div className="col-6 pt-1">
                      <span className="text-muted d-block x-small">Margem</span>
                      <strong className="text-dark fs-6">{formatPercent(d.margem)}</strong>
                    </div>
                  </div>
                </div>

                {/* DETALHAMENTO DAS LOJAS NO MOBILE */}
                {detalhesDia[d.numdia] && (
                  <div className="card-body bg-light border-top pt-2 px-2 pb-2">
                    <div className="bg-white rounded-3 p-2 border shadow-xs">
                      <div className="fw-bold text-primary fs-6 border-bottom pb-1 mb-2">
                        Filiais - Dia {d.numdia}
                      </div>
                      {detalhesDia[d.numdia].map((loja, idx) => (
                        <div key={idx} className="mb-2 p-2 bg-light rounded-3 border text-center">
                          <div className="d-flex justify-content-between align-items-center fw-bold border-bottom pb-1 mb-1">
                            <span>Loja {formatLoja(loja.filial)}</span>
                            <span className={loja.atingido >= 100 ? "text-success" : "text-custom-red"}>
                              {formatPercent(loja.atingido)}
                            </span>
                          </div>
                          <div className="row g-1 x-small text-muted">
                            <div className="col-6 border-end">Venda: <b className="text-dark">{formatMoeda(loja.venda)}</b></div>
                            <div className="col-6">Meta: <b className="text-dark">{formatMoeda(loja.meta)}</b></div>
                            <div className="col-12 border-top pt-1 mt-1">Margem: <b className="text-dark">{formatPercent(loja.margem)}</b></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* MODAL AMPLIAÇÃO DE GRÁFICO (ZOOM) */}
      <ChartZoomModal
        show={!!zoomChart}
        onClose={() => setZoomChart(null)}
        title={zoomChart?.title || 'Gráfico'}
      >
        {zoomChart?.type === 1 && (
          <Bar options={getChartOptions1(true)} data={chartData1} />
        )}
        {zoomChart?.type === 2 && (
          <Line options={getChartOptionsMargem(true)} data={chartDataMargem} />
        )}
        {zoomChart?.type === 3 && (
          <Bar options={getChartOptionsAcum(true)} data={chartDataAcum} />
        )}
      </ChartZoomModal>

      {/* MODAL SELEÇÃO DE PERÍODO */}
      <ModalGenerico
        show={showModal}
        onClose={() => setShowModal(false)}
        title="Selecionar Período"
      >
        <div className="mb-3">
          <label className="form-label fw-bold text-muted small">Selecione o Mês / Ano:</label>
          <input 
            type="month" 
            className="form-control rounded-3" 
            value={formatarParaInput(mesSelecionado)}
            onChange={(e) => {
              if(!e.target.value) return;
              const [a, m] = e.target.value.split("-");
              const novo = `${a.slice(2)}${m}`;
              setMesSelecionado(novo); 
              carregarMes(novo, lojasSelecionadas); 
              setShowModal(false);
            }} 
          />
        </div>
      </ModalGenerico>

      {/* MODAL SELEÇÃO DE LOJAS */}
      <ModalGenerico
        show={showLojaModal}
        onClose={() => setShowLojaModal(false)}
        title="Filtrar Lojas"
      >
        <div className="d-flex justify-content-between mb-3 gap-2">
          <button className="btn btn-sm btn-outline-primary fw-semibold" onClick={handleSelectAllLojas}>
            <FaCheckDouble className="me-1" /> Selecionar Todas
          </button>
          <button className="btn btn-sm btn-outline-secondary fw-semibold" onClick={handleClearAllLojas}>
            <FaTimes className="me-1" /> Limpar
          </button>
        </div>

        <div className="lojas-selection-list pe-1" style={{ maxHeight: "320px", overflowY: "auto" }}>
          {lojas.map((loja) => {
            const codStr = formatLoja(loja.cod_emp);
            const isChecked = lojasSelecionadas.includes(codStr);
            return (
              <div 
                key={loja.cod_emp} 
                className={`p-2 mb-2 rounded-3 border cursor-pointer d-flex justify-content-between align-items-center ${isChecked ? 'bg-light-blue border-primary' : 'bg-white'}`}
                onClick={() => handleLojaToggle(loja.cod_emp)}
              >
                <span className="small fw-semibold text-dark">
                  Filial {codStr} - {loja.razao}
                </span>
                <input 
                  type="checkbox" 
                  className="form-check-input cursor-pointer" 
                  checked={isChecked} 
                  onChange={() => {}} 
                />
              </div>
            );
          })}
        </div>

        <div className="mt-3 pt-2 border-top text-end">
          <button className="btn btn-custom-red btn-sm px-4 fw-semibold" onClick={() => {
            setShowLojaModal(false);
            carregarMes(mesSelecionado, lojasSelecionadas);
          }}>
            Aplicar Filtro
          </button>
        </div>
      </ModalGenerico>

      <Footer className="footer navbar-fixed-bottom" />
    </div>
  );
};

export default VendaMes;